import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Trash2, ShoppingBag, Heart, Tag } from "lucide-react";

export default function Cart() {
  const { items, remove, total } = useCart();
  const wishlist = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [eligible, setEligible] = useState([]);

  useEffect(() => {
    if (!user) { setEligible([]); return; }
    api.get("/coupons/list")
      .then(({ data }) => setEligible(data.filter((c) => c.eligible)))
      .catch(() => setEligible([]));
  }, [user]);

  const moveToWishlist = (item) => {
    if (!wishlist) return;
    wishlist.toggle({ product_id: item.product_id, name: item.name });
    remove(item.product_id, item.size, item.color);
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-16 h-16 mx-auto" />
        <h1 className="font-display text-5xl mt-4">BAG'S EMPTY</h1>
        <p className="font-mono-b mt-2 text-black/70">Time to fix that.</p>
        <Link to="/shop" data-testid="cart-empty-shop" className="inline-block mt-6 bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-3 font-mono-b font-bold uppercase">Shop the drop</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="cart-page">
      <h1 className="font-display text-5xl md:text-6xl mb-6">YOUR <span className="text-[#FF007F]">BAG</span></h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-4">
          {items.map((it) => (
            <div key={`${it.product_id}-${it.size}-${it.color}`} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 bg-white border-2 border-black brutal-shadow-sm p-3">
              <img src={it.image || "https://placehold.co/200"} alt="" className="w-24 h-32 object-cover border-2 border-black" />
              <div className="flex-1">
                <h3 className="font-display text-2xl">{it.name}</h3>
                <p className="font-mono-b text-xs text-black/60 mt-1">
                  {it.size && `SIZE: ${it.size}`} {it.color && ` · COLOR: ${it.color}`}
                </p>
                <p className="font-mono-b text-xs text-black/60 mt-2">1 of 1 · single piece</p>
              </div>
              <div className="flex flex-col items-end justify-between gap-1">
                <p className="font-mono-b font-bold text-lg">₹{it.price}</p>
                <button
                  onClick={() => moveToWishlist(it)}
                  data-testid={`cart-move-wishlist-${it.product_id}`}
                  className="text-xs font-mono-b font-bold text-black/70 hover:text-[#FF007F] flex items-center gap-1"
                >
                  <Heart className="w-3 h-3" /> SAVE
                </button>
                <button data-testid={`cart-remove-${it.product_id}`} onClick={() => remove(it.product_id, it.size, it.color)} className="text-[#FF007F]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <aside className="bg-black text-white border-2 border-black p-6 brutal-shadow h-fit" data-testid="cart-summary">
          <h2 className="font-display text-3xl mb-4 text-[#D4FF00]">SUMMARY</h2>
          <div className="flex justify-between font-mono-b py-2 border-b border-white/20"><span>Subtotal</span><span>₹{total}</span></div>
          <div className="flex justify-between font-mono-b py-2 border-b border-white/20"><span>Platform fee (7%)</span><span>₹{(total * 0.07).toFixed(2)}</span></div>
          <div className="flex justify-between font-mono-b py-2 border-b border-white/20"><span>Shipping</span><span>₹99</span></div>

          {eligible.length > 0 && (
            <div className="mt-3 border-2 border-dashed border-[#D4FF00] p-3" data-testid="cart-coupon-hint">
              <p className="text-[10px] font-mono-b font-bold uppercase text-[#D4FF00] tracking-wider flex items-center gap-1">
                <Tag className="w-3 h-3" /> DEAL UNLOCKED
              </p>
              {eligible.map((c) => {
                const preview = +(total * c.percent / 100).toFixed(2);
                return (
                  <div key={c.code} className="mt-2" data-testid={`cart-coupon-${c.code}`}>
                    <p className="font-display text-lg leading-none text-[#D4FF00]">{c.code}</p>
                    <p className="text-[10px] font-mono-b text-white/70">{c.label} · save ₹{preview} at checkout</p>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-between font-mono-b font-bold py-4 text-2xl"><span>TOTAL</span><span className="text-[#D4FF00]">₹{(total + total * 0.07 + 99).toFixed(2)}</span></div>
          <button onClick={() => navigate("/checkout")} data-testid="cart-checkout" className="w-full bg-[#D4FF00] text-black border-2 border-white brutal-shadow hover-nudge py-3 mt-4 font-mono-b font-bold uppercase">
            Checkout
          </button>
        </aside>
      </div>
    </div>
  );
}
